import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type LegalDoc = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = {
  title: 'Política de Privacidad',
  body: [{
    h: '1. Información que Recopilamos',
    p: 'Recopilamos la información que nos proporcionas voluntariamente al realizar una compra o suscribirte a nuestros recursos, incluyendo nombre, dirección de correo electrónico y datos de pago procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    h: '2. Uso de la Información',
    p: 'Utilizamos tu información exclusivamente para entregarte el producto adquirido, mejorar nuestros materiales educativos y enviarte actualizaciones relevantes sobre el sistema "Crédito Maestro 2026". Nunca venderemos ni compartiremos tus datos con terceros con fines comerciales.'
  }, {
    h: '3. Cookies y Tecnologías de Seguimiento',
    p: 'Nuestro sitio utiliza cookies para mejorar tu experiencia de navegación, recordar tus preferencias y analizar el tráfico del sitio. Puedes desactivar las cookies desde la configuración de tu navegador.'
  }, {
    h: '4. Seguridad de los Datos',
    p: 'Implementamos medidas de seguridad estándar de la industria, incluyendo encriptación SSL de 256 bits, para proteger tu información personal y financiera contra accesos no autorizados.'
  }, {
    h: '5. Tus Derechos',
    p: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. También puedes solicitar la portabilidad de tus datos o retirar tu consentimiento para el procesamiento de los mismos.'
  }, {
    h: '6. Cambios en la Política',
    p: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en este sitio.'
  }]
};
const TERMS_CONTENT = {
  title: 'Términos y Condiciones',
  body: [{
    h: '1. Aceptación de los Términos',
    p: 'Al acceder a este sitio web y/o adquirir el producto "Crédito Maestro 2026", aceptas estar sujeto a estos términos y condiciones, así como a todas las leyes y regulaciones aplicables.'
  }, {
    h: '2. Naturaleza del Producto',
    p: 'El sistema "Crédito Maestro 2026" es un producto digital de naturaleza estrictamente educativa e informativa. No constituye asesoría legal, financiera, contable ni profesional de ningún tipo. Los resultados varían según cada situación individual.'
  }, {
    h: '3. Sin Garantía de Resultados',
    p: 'Si bien nuestro sistema está basado en derechos otorgados por la Ley FCRA (Fair Credit Reporting Act), no garantizamos resultados específicos. La eliminación de información de tu reporte de crédito depende de múltiples factores fuera de nuestro control, incluyendo la respuesta de los burós y la exactitud de la información disputada.'
  }, {
    h: '4. Licencia de Uso',
    p: 'Al adquirir el producto, recibes una licencia personal, no transferible y no exclusiva para uso individual. Está estrictamente prohibida la reproducción, redistribución, reventa o modificación del contenido sin autorización expresa por escrito.'
  }, {
    h: '5. Política de Reembolso',
    p: 'Debido a la naturaleza digital del producto y al acceso inmediato al material protegido por derechos de autor, todas las ventas son finales y no se ofrecen reembolsos una vez completada la transacción.'
  }, {
    h: '6. Limitación de Responsabilidad',
    p: 'En ningún caso seremos responsables por daños directos, indirectos, incidentales o consecuentes derivados del uso o incapacidad de uso del producto. El usuario es el único responsable de sus decisiones financieras y legales.'
  }, {
    h: '7. Propiedad Intelectual',
    p: 'Todo el contenido del sitio web y del producto, incluyendo textos, gráficos, logos, plantillas y guías, está protegido por leyes de derechos de autor y es propiedad exclusiva de Crédito Maestro.'
  }, {
    h: '8. Ley Aplicable',
    p: 'Estos términos se rigen por las leyes aplicables en los Estados Unidos de América. Cualquier disputa será resuelta mediante arbitraje vinculante.'
  }]
};
interface LegalModalProps {
  doc: LegalDoc;
  onClose: () => void;
}
function LegalModal({
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (doc) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [doc]);
  const content = doc === 'privacy' ? PRIVACY_CONTENT : doc === 'terms' ? TERMS_CONTENT : null;
  return <AnimatePresence data-id="element-42">
      {content && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-43">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className="bg-brand-card border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden" data-id="element-44">
            <div className="flex items-center justify-between p-6 border-b border-gray-800 sticky top-0 bg-brand-card z-10" data-id="element-45">
              <h3 className="text-2xl font-display uppercase text-white" data-id="element-46">
                {content.title}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-800" aria-label="Cerrar" data-id="element-47">
                <X className="w-6 h-6" data-id="element-48" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 md:p-8 space-y-6" data-id="element-49">
              <p className="text-xs text-gray-500 uppercase tracking-wider" data-id="element-50">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
              {content.body.map((section, idx) => <div key={idx} data-id="element-51">
                  <h4 className="text-lg font-bold text-brand-green mb-2" data-id="element-52">
                    {section.h}
                  </h4>
                  <p className="text-gray-300 leading-relaxed text-sm md:text-base" data-id="element-53">
                    {section.p}
                  </p>
                </div>)}
            </div>

            <div className="p-4 border-t border-gray-800 bg-brand-darker" data-id="element-54">
              <button onClick={onClose} className="w-full py-3 bg-brand-green text-brand-dark font-bold uppercase rounded-lg hover:bg-white transition-colors text-sm tracking-wide" data-id="element-55">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [openDoc, setOpenDoc] = useState<LegalDoc>(null);
  return <>
      <footer className="bg-[#05070A] py-12 border-t border-gray-900" data-id="element-56">
        <div className="container mx-auto px-4 max-w-5xl text-center" data-id="element-57">
          <div className="text-2xl font-display text-white mb-6 uppercase tracking-widest" data-id="element-58">
            Crédito <span className="text-brand-green" data-id="element-59">Maestro</span>
          </div>

          <div className="text-xs text-gray-600 leading-relaxed max-w-3xl mx-auto space-y-4" data-id="element-60">
            <p data-id="element-61">
              DESCARGO DE RESPONSABILIDAD: El contenido de este sitio web y del
              producto "Crédito Maestro 2026" es estrictamente para fines
              educativos e informativos. No constituye asesoría legal,
              financiera ni profesional.
            </p>
            <p data-id="element-62">
              Los resultados varían según la situación individual de cada
              persona, la exactitud de la información disputada y el
              cumplimiento de los pasos descritos. No garantizamos la
              eliminación de información precisa y verificable de su reporte de
              crédito.
            </p>
            <p data-id="element-63">
              Al utilizar este sitio y comprar nuestros productos, usted acepta
              que es el único responsable de sus propias decisiones y acciones
              financieras.
            </p>
          </div>

          <div className="mt-8 flex items-center justify-center gap-6 text-xs" data-id="element-64">
            <button onClick={() => setOpenDoc('privacy')} className="text-gray-500 hover:text-brand-green transition-colors uppercase tracking-wider font-semibold" data-id="element-65">
              Política de Privacidad
            </button>
            <span className="text-gray-800" data-id="element-66">|</span>
            <button onClick={() => setOpenDoc('terms')} className="text-gray-500 hover:text-brand-green transition-colors uppercase tracking-wider font-semibold" data-id="element-67">
              Términos y Condiciones
            </button>
          </div>

          <div className="mt-6 text-xs text-gray-700" data-id="element-68">
            &copy; {new Date().getFullYear()} Crédito Maestro. Todos los
            derechos reservados.
          </div>
        </div>
      </footer>

      <LegalModal doc={openDoc} onClose={() => setOpenDoc(null)} data-id="element-69" />
    </>;
}